<template>
  <v-app v-if="!scanningQRCode">
    <v-main style="padding-bottom: 60px" v-resize="onResize">
      <div style="position: absolute; z-index: 9999999; top: 0; right: 4px; font-size: 10px">
        {{ version }}
      </div>
      <KioskSplashScreen v-once style="z-index: 2000" />
      <MaintenanceModeScreen v-once style="z-index: 1500" />
      <NetworkConnectionErrorScreen v-once style="z-index: 1450" />
      <InitialLoadingScreen v-once style="z-index: 1400" />

      <router-view style="z-index: 500" />
      <LocationPackagingSelectionView v-once style="z-index: 1000" />
      <ConfirmActivityView style="z-index: 2001" @reset="userInactive" @confirm="userConfirmActive" v-once />
      <ClosedScreen v-once style="z-index: 2500" />
    </v-main>
    <BottomDrawer @reset="reset" style="z-index: 900" />
  </v-app>
</template>

<script>
import _ from 'lodash'
import { useTheme } from 'vuetify'
import { useStore } from './stores/store'

import { name, version } from '../package.json'

// Capacitor imports
import { BarcodeScanner } from '@capacitor-community/barcode-scanner'
import { Capacitor } from '@capacitor/core'
import { Network } from '@capacitor/network'
import { Preferences } from '@capacitor/preferences'
import { createClient } from 'graphql-ws'

import BottomDrawer from '@/components/drawer/BottomDrawer.vue'
import { DateTime } from 'luxon'
import ClosedScreen from './views/ClosedScreen.vue'
import ConfirmActivityView from './views/ConfirmActivityView.vue'
import InitialLoadingScreen from './views/InitialLoadingScreen.vue'
import KioskSplashScreen from './views/KioskSplashScreen.vue'
import LocationPackagingSelectionView from './views/LocationPackagingSelectionView.vue'
import MaintenanceModeScreen from './views/MaintenanceModeScreen.vue'
import NetworkConnectionErrorScreen from './views/NetworkConnectionErrorScreen.vue'

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export default {
  name: 'App',
  components: {
    KioskSplashScreen,
    NetworkConnectionErrorScreen,
    ConfirmActivityView,
    InitialLoadingScreen,
    MaintenanceModeScreen,
    LocationPackagingSelectionView,
    ClosedScreen,
    BottomDrawer,
  },
  setup() {
    console.log(`SalesCloud Kiosk v${version}`)
    const store = useStore()
    const theme = useTheme()
    return {
      store,
      theme,
    }
  },
  data() {
    return {
      intervalId: null,
      minuteCounter: 0,
      secondsCounter: 0,
      minutesUntilKioskFetch: 60,
      secondsUntilSystemTimeFetch: 60,
      millisecondsInSecond: 1000,
      millisecondsInMinute: 60000,
      lastPrinterStatusCheckInMillis: 0,
    }
  },
  computed: {
    gettingInitialData() {
      return this.store.gettingInitialData
    },
    kiosk() {
      return this.store.kiosk
    },
    organization() {
      if (typeof this.kiosk !== 'undefined' && this.kiosk !== null) {
        return this.kiosk.organization
      }
      return null
    },
    version() {
      return version
    },
    maintenanceMode() {
      if (typeof this.kiosk !== 'undefined' && this.kiosk !== null) {
        return this.kiosk.maintenanceMode
      }
      return false
    },
    showSelectionView() {
      return this.store.showSelectionView
    },
    showConfirmActivity() {
      return this.store.showConfirmActivity
    },
    awaitingPayment() {
      return this.store.awaitingPayment
    },
    scanningQRCode() {
      return this.store.scanningQRCode
    },
    systemTimeInMillis() {
      return this.store.systemTimeInMillis
    },
  },
  watch: {
    systemTimeInMillis(currentValue, previousValue) {
      if (currentValue !== previousValue) {
        this.store.checkKioskOpenStatus()

        // Only update printer status every 60 seconds
        if (previousValue && (currentValue - this.lastPrinterStatusCheckInMillis >= 60000)) {
          this.store.updatePrinterStatus()
          this.lastPrinterStatusCheckInMillis = currentValue
        }
      }
    },
    kiosk(value) {
      window.kiosk = value
    },
    organization(value) {
      if (value) {
        let primaryColor = null
        let accentColor = null

        // Setting primary color
        // Widget settings take priority over organization settings
        if (typeof this.store.kiosk !== 'undefined' && this.store.kiosk !== null && this.store.kiosk.primaryColor) {
          primaryColor = this.store.kiosk.primaryColor
        } else if (value.primary_color) {
          primaryColor = value.primary_color
        }

        // Setting accent color
        if (this.store.kiosk && this.store.kiosk.accentColor) {
          accentColor = this.store.kiosk.accentColor
        } else if (value.accent_color) {
          accentColor = value.accent_color
        }

        if (primaryColor) {
          this.$vuetify.theme.themes.light.primary = primaryColor
          this.$vuetify.theme.themes.light.colors.primary = primaryColor

          this.$vuetify.theme.themes.dark.primary = primaryColor
          this.$vuetify.theme.themes.dark.colors.primary = primaryColor
        }

        if (accentColor) {
          this.$vuetify.theme.themes.light.accent = accentColor
          this.$vuetify.theme.themes.light.colors.accent = accentColor

          this.$vuetify.theme.themes.dark.accent = accentColor
          this.$vuetify.theme.themes.dark.colors.accent = accentColor
        }
        this.store.showSelectionView = true

        this.removeEventListeners()
        if (this.store.kiosk && !this.store.kiosk.maintenanceMode) {
          this.setEventListeners()
          // We do not want to null set the order unless we have to
          if (!this.store.order || Object.keys(this.store.order).length < 1) {
            this.store.order = {}
          }
        }
      }
    },
    showConfirmActivity(value) {
      if (value === true) {
        this.store.customLogger('info', 'Showing confirm activity')
        this.removeEventListeners()
      }
    },
    awaitingPayment(currentValue, previousValue) {
      if (currentValue === true) {
        this.removeEventListeners()
      } else if (previousValue === true && currentValue === false) {
        this.setEventListeners()
      }
    },
  },
  methods: {
    onResize: _.debounce(function () {
      this.store.setIsLandscape(window.innerWidth > window.innerHeight)
    }, 500),
    async setKioskUUID(uuid) {
      if (typeof uuid !== 'undefined' && uuid !== null) {
        try {
          await Preferences.set({ key: 'sc-kiosk', value: uuid })
        } catch (e) {
          //
        }
      }
      return true
    },
    async checkPermission() {
      try {
        // check if user already granted permission
        const status = await BarcodeScanner.checkPermission({
          force: false,
        })

        if (status.granted) {
          // user granted permission
          return true
        }

        if (status.denied) {
          // user denied permission
          return false
        }

        // user has not denied permission
        // but the user also has not yet granted the permission
        // so request it
        const statusRequest = await BarcodeScanner.checkPermission({
          force: true,
        })

        if (statusRequest.granted) {
          // the user did grant the permission now
          return true
        }

        // user did not grant the permission, so he must have declined the request
        return false
      } catch (e) {
        return false
      }
    },
    async openCamera() {
      const permission = await this.checkPermission()

      if (permission) {
        try {
          this.store.scanningQRCode = true
          await BarcodeScanner.hideBackground()
          const result = await BarcodeScanner.startScan()

          if (result.hasContent) {
            // Stop the scan here
            await BarcodeScanner.showBackground()
            await BarcodeScanner.stopScan()
            this.store.scanningQRCode = false

            await this.setKioskUUID(result.content)
            this.store.kioskUUID = result.content
            await this.store.getKioskData(result.content)
          }
        } catch (e) {
          this.store.customLogger('info', 'Camera could not be opened')
          //
        }
      }
    },
    async getValueFromPreferences(key) {
      try {
        const { value } = await Preferences.get({ key: key })
        return value
      } catch {
        return null
      }
    },
    getValueFromWindow(key) {
      try {
        const urlParams = new URLSearchParams(window.location.search)
        return urlParams.get(key)
      } catch {
        return null
      }
    },
    userInactive() {
      this.store.customLogger('info', 'User is inactive, triggering reset')
      this.reset()
    },
    reset() {
      this.store.reset()
      this.setEventListeners()
    },
    userConfirmActive() {
      this.store.showConfirmActivity = false
      this.setEventListeners()
    },
    resetActivityCheck() {
      this.store.showConfirmActivity = false
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
      }
      this.timeoutId = setTimeout(() => {
        if (this.store.canShowConfirmActivity) {
          this.store.showConfirmActivity = true
        }
      }, 60000)
    },
    clearTimeout() {
      this.store.showConfirmActivity = false
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
      }
    },
    removeEventListeners() {
      window.removeEventListener('touchstart', this.clearTimeout)
      window.removeEventListener('touchmove', this.clearTimeout)
      window.removeEventListener('touchend', this.resetActivityCheck)
      window.removeEventListener('touchcancel', this.resetActivityCheck)
      window.removeEventListener('mousedown', this.clearTimeout)
      window.removeEventListener('mouseup', this.resetActivityCheck)
    },
    setEventListeners() {
      window.addEventListener('touchstart', this.clearTimeout)
      window.addEventListener('touchmove', this.clearTimeout)
      window.addEventListener('touchend', this.resetActivityCheck)
      window.addEventListener('touchcancel', this.resetActivityCheck)
      window.addEventListener('mousedown', this.clearTimeout)
      window.addEventListener('mouseup', this.resetActivityCheck)
    },
    getKioskData() {
      this.store.customLogger('info', 'Fetching Kiosk Data')
      this.store.getKioskData()
      this.minuteCounter = 0
    },
  },
  async mounted() {
    this.store.customLogger('info', 'Mounting Kiosk')
    let uuid = null
    let previewObject = null

    if (Capacitor.getPlatform() === 'android') {
      uuid = await this.getValueFromPreferences('sc-kiosk')
    } else {
      uuid = this.getValueFromWindow('uuid')
      previewObject = this.getValueFromWindow('previewDataObject')
    }

    try {
      Network.addListener('networkStatusChange', (status) => {
        if (status.connected === false) {
          this.store.showNoNetwork = true
          this.store.hasNoNetwork = true
        } else {
          this.store.showNoNetwork = false
          this.store.hasNoNetwork = false
          // Fetch data
        }
      })

      const status = await Network.getStatus()
      if (status.connected === false) {
        this.store.showNoNetwork = true
        this.store.hasNoNetwork = true
      } else {
        // Fetch data?
      }
    } catch (e) {
      //
    }

    // Making sure we have no left over interval
    clearInterval(this.intervalId)

    // We count every minute to check the date and time
    // Every 60 minutes we get the kiosk data
    this.intervalId = setInterval(() => {
      this.secondsCounter += 1

      if (this.secondsCounter >= this.secondsUntilSystemTimeFetch) {
        this.store.getSystemTimeInMillis()
        this.secondsCounter = 0
        this.minuteCounter += 1
      } else {
        if (this.systemTimeInMillis) {
          this.store.systemTimeInMillis = this.systemTimeInMillis + this.millisecondsInSecond
        } else {
          this.store.systemTimeInMillis = DateTime.local().toMillis()
        }
      }

      // We don't want to get the system time in millis when we also get the kiosk data
      if (this.minuteCounter >= 60) {
        this.getKioskData()
      }
    }, this.millisecondsInSecond)

    if (uuid) {
      this.store.gettingInitialData = true
      this.store.kioskUUID = uuid

      await delay(5000)
      this.store.customLogger('info', 'Initializing Kiosk')
      this.result = await this.store.getKioskData(uuid)
      this.store.socketClient = createClient({
        url: this.store.socketURL,
        connectionParams: () => {
          return {
            headers: {
              Authorization: 'anonymous',
              'x-sc-client-name': name,
              'x-sc-client-version': version,
            },
          }
        },
      })

      this.store.subscribeToKiosk()
    }

    if (previewObject) {
      try {
        this.store.previewValues = JSON.parse(previewObject)
        await this.store.getPreviewData()
      } catch {
        // Do nothing
      }
    }
  },
}
</script>

<style>
html * {
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.disable-pointer-events,
.disable-pointer-events * {
  pointer-events: none !important;
}

.disable-pointer-events-inner * {
  pointer-events: none !important;
}
</style>
